import {
  ArrowLeft, Download, BarChart3, Calendar, Briefcase, TrendingUp,
  HardHat, Pencil, Check, X as XIcon, Loader2
} from 'lucide-react';
import { fmtEurCompact } from '../../utils/formatUtils';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import type {
  ProjectStats, ProjectHistory, ProjectFinancials, MonthlyHistoryPoint, HistoryViewMode
} from './types';
import type { ChartData } from 'chart.js';

// Enregistrement Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, LineElement, PointElement);

interface HistoriqueTabProps {
  projectStats: ProjectStats[];
  projectHistory: ProjectHistory | null;
  projectFinancials: ProjectFinancials | null;
  historyProject: string;
  setHistoryProject: (id: string) => void;
  resetHistoryView: () => void;
  historyViewMode: HistoryViewMode;
  setHistoryViewMode: (mode: HistoryViewMode) => void;
  showCumulative: boolean;
  setShowCumulative: (show: boolean) => void;
  historyLoading: boolean;
  editingDevis: boolean;
  setEditingDevis: (editing: boolean) => void;
  devisInputValue: string;
  setDevisInputValue: (value: string) => void;
  savingDevis: boolean;
  handleSaveDevis: () => void;
  historyChartData: ChartData<'bar'> | null;
  aggregateByYear: (data: MonthlyHistoryPoint[]) => MonthlyHistoryPoint[];
  handleExportHistoryPDF: () => void;
  projectList: { id: string; code: string; name: string; category?: string }[];
}

export default function HistoriqueTab({
  projectStats, projectHistory, projectFinancials,
  historyProject, setHistoryProject, resetHistoryView,
  historyViewMode, setHistoryViewMode, showCumulative, setShowCumulative,
  historyLoading,
  editingDevis, setEditingDevis, devisInputValue, setDevisInputValue,
  savingDevis, handleSaveDevis,
  historyChartData, aggregateByYear,
  handleExportHistoryPDF, projectList
}: HistoriqueTabProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

      {/* Sélecteur de projet et contrôles */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          {/* Sélecteur projet */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: '300px' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Briefcase size={16} /> Sélectionner une Affaire
            </label>
            <select
              value={historyProject}
              onChange={(e) => setHistoryProject(e.target.value)}
              style={{
                padding: '12px 16px',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                fontSize: '0.95rem',
                cursor: 'pointer',
                background: 'white'
              }}
            >
              <option value="">-- Vue globale --</option>
              {projectList
                .filter(p => p.category !== 'divers')
                .map(p => (
                  <option key={p.id} value={p.id}>{p.code} - {p.name}</option>
                ))}
            </select>
          </div>

          {/* Toggle vue mensuelle/annuelle */}
          {projectHistory && (
            <div style={{ display: 'flex', gap: '8px', background: '#f1f5f9', padding: '4px', borderRadius: '10px' }}>
              <button
                onClick={() => setHistoryViewMode('monthly')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.85rem',
                  background: historyViewMode === 'monthly' ? '#059669' : 'transparent',
                  color: historyViewMode === 'monthly' ? 'white' : '#64748b',
                  transition: 'all 0.2s'
                }}
              >
                Mensuel
              </button>
              <button
                onClick={() => setHistoryViewMode('yearly')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.85rem',
                  background: historyViewMode === 'yearly' ? '#059669' : 'transparent',
                  color: historyViewMode === 'yearly' ? 'white' : '#64748b',
                  transition: 'all 0.2s'
                }}
              >
                Annuel
              </button>
            </div>
          )}

          {/* Toggle cumul */}
          {projectHistory && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showCumulative}
                onChange={(e) => setShowCumulative(e.target.checked)}
                style={{ width: '18px', height: '18px', accentColor: '#059669' }}
              />
              <span style={{ fontSize: '0.9rem', color: '#475569', fontWeight: '500' }}>Afficher cumul</span>
            </label>
          )}

          {/* Bouton export */}
          {projectHistory && (
            <button
              onClick={handleExportHistoryPDF}
              style={{
                padding: '12px 20px',
                background: '#334155',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Download size={16} /> PDF Historique
            </button>
          )}
        </div>
      </div>

      {/* État de chargement */}
      {historyLoading && (
        <div style={{
          padding: '80px',
          textAlign: 'center',
          color: '#64748b',
          background: 'white',
          borderRadius: '16px',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e2e8f0',
            borderTopColor: '#059669',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          Chargement de l'historique...
        </div>
      )}

      {/* Vue globale — KPIs et tableau récapitulatif */}
      {!historyProject && !historyLoading && (() => {
        const statsWithDevis = projectStats.filter(p => p.devisAmount != null && p.devisAmount > 0);
        const caTotal = statsWithDevis.reduce((s, p) => s + (p.devisAmount || 0), 0);
        const coutsTotal = projectStats.reduce((s, p) => s + p.totalMoCost + p.totalAchats, 0);
        const margeGlobale = caTotal > 0 ? caTotal - coutsTotal : 0;
        const margePctGlobale = caTotal > 0 ? (margeGlobale / caTotal) * 100 : 0;
        const fmtEurG = fmtEurCompact;
        const margeGColor = margePctGlobale >= 25 ? '#16a34a' : margePctGlobale >= 10 ? '#d97706' : '#dc2626';
        const margeGBg = margePctGlobale >= 25 ? '#f0fdf4' : margePctGlobale >= 10 ? '#fffbeb' : '#fef2f2';
        const margeGBorder = margePctGlobale >= 25 ? '#bbf7d0' : margePctGlobale >= 10 ? '#fde68a' : '#fecaca';

        // Trier par CA décroissant (projets avec devis en premier)
        const sortedProjects = [...projectStats].sort((a, b) => {
          if (a.devisAmount != null && b.devisAmount == null) return -1;
          if (a.devisAmount == null && b.devisAmount != null) return 1;
          return (b.devisAmount || 0) - (a.devisAmount || 0);
        });

        return (
          <>
            {/* KPIs globaux */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
              {/* CA Total */}
              <div style={{ background: 'white', borderRadius: '16px', padding: '20px', border: '1px solid #fde68a', boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                  Chiffre d'Affaires
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: '800', color: '#78350f' }}>
                  {fmtEurG(caTotal)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#b45309', marginTop: '4px' }}>
                  {statsWithDevis.length} affaire{statsWithDevis.length > 1 ? 's' : ''} avec devis
                </div>
              </div>

              {/* Coûts Totaux */}
              <div style={{ background: 'white', borderRadius: '16px', padding: '20px', border: '1px solid #bfdbfe', boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                  Coûts Totaux
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: '800', color: '#1e40af' }}>
                  {fmtEurG(coutsTotal)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#3b82f6', marginTop: '4px' }}>
                  MO : {fmtEurG(projectStats.reduce((s, p) => s + p.totalMoCost, 0))} · Achats : {fmtEurG(projectStats.reduce((s, p) => s + p.totalAchats, 0))}
                </div>
              </div>

              {/* Marge Globale */}
              <div style={{ background: 'white', borderRadius: '16px', padding: '20px', border: `1px solid ${margeGBorder}`, boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: margeGColor, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                  Marge Globale
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: '800', color: margeGColor }}>
                  {caTotal > 0 ? fmtEurG(margeGlobale) : '—'}
                </div>
                {caTotal === 0 && (
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '4px', fontStyle: 'italic' }}>
                    Aucun devis renseigné
                  </div>
                )}
              </div>

              {/* Marge % */}
              <div style={{ background: margeGBg, borderRadius: '16px', padding: '20px', border: `1px solid ${margeGBorder}`, boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: margeGColor, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                  Marge Moyenne
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: '800', color: margeGColor }}>
                  {caTotal > 0 ? `${margePctGlobale.toFixed(1)}%` : '—'}
                </div>
                {caTotal > 0 && (
                  <div style={{ fontSize: '0.75rem', color: margeGColor, marginTop: '4px' }}>
                    pondérée sur le CA
                  </div>
                )}
              </div>
            </div>

            {/* Tableau récapitulatif des affaires */}
            <div style={{
              background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0',
              overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.03)'
            }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <BarChart3 size={18} color="#059669" /> Récapitulatif par Affaire
                </h3>
                <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                  {projectStats.length} affaire{projectStats.length > 1 ? 's' : ''}
                </span>
              </div>
              {projectStats.length === 0 ? (
                <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>
                  <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
                  <p style={{ margin: 0 }}>Chargement des données...</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>Affaire</th>
                        <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '0.72rem', color: '#92400e', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>Devis HT</th>
                        <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '0.72rem', color: '#1d4ed8', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>MO Réelle</th>
                        <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '0.72rem', color: '#6d28d9', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>Achats</th>
                        <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '0.72rem', color: '#475569', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>Marge €</th>
                        <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '0.72rem', color: '#475569', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>Marge %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedProjects.map((p, idx) => {
                        const mc = p.margePercent == null ? '#64748b'
                          : p.margePercent >= 25 ? '#16a34a'
                          : p.margePercent >= 10 ? '#d97706'
                          : '#dc2626';
                        const mbg = p.margePercent == null ? 'transparent'
                          : p.margePercent >= 25 ? '#f0fdf4'
                          : p.margePercent >= 10 ? '#fffbeb'
                          : '#fef2f2';

                        return (
                          <tr
                            key={p.id}
                            onClick={() => setHistoryProject(p.id)}
                            style={{
                              borderBottom: idx < sortedProjects.length - 1 ? '1px solid #f1f5f9' : 'none',
                              cursor: 'pointer', transition: 'background 0.15s'
                            }}
                            onMouseOver={e => { e.currentTarget.style.background = '#f0fdf4'; }}
                            onMouseOut={e => { e.currentTarget.style.background = idx % 2 === 0 ? 'white' : '#fafafa'; }}
                          >
                            <td style={{ padding: '14px 16px' }}>
                              <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#1e293b', marginBottom: '2px' }}>{p.name}</div>
                              <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontFamily: 'monospace' }}>{p.code}</div>
                            </td>
                            <td style={{ padding: '14px', textAlign: 'right', fontWeight: '600', color: p.devisAmount != null ? '#78350f' : '#cbd5e1' }}>
                              {p.devisAmount != null ? fmtEurG(p.devisAmount) : '—'}
                            </td>
                            <td style={{ padding: '14px', textAlign: 'right', color: '#1e40af', fontWeight: '500' }}>
                              {p.totalMoCost > 0 ? fmtEurG(p.totalMoCost) : '—'}
                            </td>
                            <td style={{ padding: '14px', textAlign: 'right', color: '#5b21b6', fontWeight: '500' }}>
                              {p.totalAchats > 0 ? fmtEurG(p.totalAchats) : '—'}
                            </td>
                            <td style={{ padding: '14px', textAlign: 'right', fontWeight: '700', color: mc }}>
                              {p.marge != null ? fmtEurG(p.marge) : '—'}
                            </td>
                            <td style={{ padding: '14px', textAlign: 'right' }}>
                              {p.margePercent != null ? (
                                <span style={{
                                  display: 'inline-block',
                                  padding: '4px 10px',
                                  borderRadius: '8px',
                                  background: mbg,
                                  color: mc,
                                  fontWeight: '700',
                                  fontSize: '0.85rem'
                                }}>
                                  {p.margePercent.toFixed(1)}%
                                </span>
                              ) : (
                                <span style={{ color: '#cbd5e1' }}>—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {/* Footer avec totaux */}
                    {statsWithDevis.length > 0 && (
                      <tfoot style={{ background: '#f1f5f9' }}>
                        <tr>
                          <td style={{ padding: '14px 16px', fontWeight: '800', color: '#1e293b' }}>TOTAL</td>
                          <td style={{ padding: '14px', textAlign: 'right', fontWeight: '800', color: '#78350f' }}>
                            {fmtEurG(caTotal)}
                          </td>
                          <td style={{ padding: '14px', textAlign: 'right', fontWeight: '700', color: '#1e40af' }}>
                            {fmtEurG(projectStats.reduce((s, p) => s + p.totalMoCost, 0))}
                          </td>
                          <td style={{ padding: '14px', textAlign: 'right', fontWeight: '700', color: '#5b21b6' }}>
                            {fmtEurG(projectStats.reduce((s, p) => s + p.totalAchats, 0))}
                          </td>
                          <td style={{ padding: '14px', textAlign: 'right', fontWeight: '800', color: margeGColor }}>
                            {fmtEurG(margeGlobale)}
                          </td>
                          <td style={{ padding: '14px', textAlign: 'right' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 12px',
                              borderRadius: '8px',
                              background: margeGBg,
                              color: margeGColor,
                              fontWeight: '800',
                              fontSize: '0.9rem'
                            }}>
                              {margePctGlobale.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>
          </>
        );
      })()}

      {/* Contenu principal - Historique du projet */}
      {projectHistory && !historyLoading && (
        <>
          {/* Bouton retour vue globale */}
          <button
            onClick={resetHistoryView}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 18px', background: 'white', border: '1px solid #e2e8f0',
              borderRadius: '10px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: '600',
              color: '#059669', width: 'fit-content', transition: 'all 0.15s'
            }}
            onMouseOver={e => { e.currentTarget.style.background = '#ecfdf5'; e.currentTarget.style.borderColor = '#059669'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
          >
            <ArrowLeft size={16} /> Retour vue globale
          </button>

          {/* Header projet */}
          <div style={{
            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
            borderRadius: '16px',
            padding: '24px',
            color: 'white',
            boxShadow: '0 4px 6px -1px rgba(5, 150, 105, 0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
              <div>
                <div style={{ fontSize: '0.8rem', opacity: 0.9, marginBottom: '4px' }}>
                  {projectHistory.projectCode}
                </div>
                <h2 style={{ margin: '0 0 8px', fontSize: '1.5rem', fontWeight: '800', color: 'inherit' }}>
                  {projectHistory.projectName}
                </h2>
                <div style={{ display: 'flex', gap: '12px', fontSize: '0.85rem', opacity: 0.9, flexWrap: 'wrap' }}>
                  <span>Créé le {new Date(projectHistory.createdAt).toLocaleDateString('fr-FR')}</span>
                  <span>|</span>
                  <span>{projectHistory.spanMonths} mois d'activité</span>
                  {!projectHistory.isActive && (
                    <>
                      <span>|</span>
                      <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: '4px' }}>
                        ARCHIVÉ
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: '800' }}>
                  {projectHistory.totalHours.total.toFixed(2)}
                </div>
                <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>heures totales</div>
              </div>
            </div>
          </div>

          {/* ── SYNTHÈSE FINANCIÈRE ── */}
          {projectFinancials && (() => {
            const { devisAmount, totalAchats, totalMoCost, marge, margePercent } = projectFinancials;
            const fmtEur = fmtEurCompact;
            const margeColor = margePercent == null ? '#64748b' : margePercent >= 25 ? '#16a34a' : margePercent >= 10 ? '#d97706' : '#dc2626';
            const margeBg = margePercent == null ? '#f8fafc' : margePercent >= 25 ? '#f0fdf4' : margePercent >= 10 ? '#fffbeb' : '#fef2f2';
            const margeBorder = margePercent == null ? '#e2e8f0' : margePercent >= 25 ? '#bbf7d0' : margePercent >= 10 ? '#fde68a' : '#fecaca';

            return (
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '20px 24px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.03)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    <TrendingUp size={16} color="#475569" /> Synthèse Financière
                  </div>
                  {/* Bouton modifier devis */}
                  {!editingDevis && (
                    <button
                      onClick={() => { setEditingDevis(true); setDevisInputValue(devisAmount != null ? String(devisAmount) : ''); }}
                      style={{ padding: '6px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', color: '#475569', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Pencil size={13} /> {devisAmount != null ? 'Modifier le devis' : 'Saisir le devis'}
                    </button>
                  )}
                  {editingDevis && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="number"
                        min="0"
                        step="1000"
                        value={devisInputValue}
                        onChange={e => setDevisInputValue(e.target.value)}
                        placeholder="Ex: 287000"
                        style={{ width: '140px', padding: '6px 12px', border: '1px solid #fcd34d', borderRadius: '8px', fontSize: '0.9rem' }}
                        autoFocus
                      />
                      <span style={{ fontSize: '0.85rem', color: '#64748b', whiteSpace: 'nowrap' }}>€ HT</span>
                      <button
                        onClick={handleSaveDevis}
                        disabled={savingDevis}
                        style={{ padding: '6px 12px', background: '#059669', color: 'white', border: 'none', borderRadius: '8px', cursor: savingDevis ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
                      >
                        {savingDevis ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={14} />}
                      </button>
                      <button
                        onClick={() => setEditingDevis(false)}
                        style={{ padding: '6px 10px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      >
                        <XIcon size={14} color="#64748b" />
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
                  {/* Devis HT */}
                  <div style={{ background: devisAmount != null ? '#fffbeb' : '#f8fafc', borderRadius: '12px', padding: '16px', border: `1px solid ${devisAmount != null ? '#fde68a' : '#e2e8f0'}` }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: devisAmount != null ? '#92400e' : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                      Devis HT
                    </div>
                    <div style={{ fontSize: '1.4rem', fontWeight: '800', color: devisAmount != null ? '#78350f' : '#cbd5e1' }}>
                      {devisAmount != null ? fmtEur(devisAmount) : '—'}
                    </div>
                  </div>

                  {/* MO Réelle */}
                  <div style={{ background: '#eff6ff', borderRadius: '12px', padding: '16px', border: '1px solid #bfdbfe' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                      MO Réelle
                    </div>
                    <div style={{ fontSize: '1.4rem', fontWeight: '800', color: '#1e40af' }}>
                      {fmtEur(totalMoCost)}
                    </div>
                    {totalMoCost === 0 && (
                      <div style={{ fontSize: '0.7rem', color: '#93c5fd', marginTop: '4px', fontStyle: 'italic' }}>
                        (taux horaires non renseignés)
                      </div>
                    )}
                  </div>

                  {/* Achats */}
                  <div style={{ background: '#f5f3ff', borderRadius: '12px', padding: '16px', border: '1px solid #ddd6fe' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#6d28d9', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                      Achats
                    </div>
                    <div style={{ fontSize: '1.4rem', fontWeight: '800', color: '#5b21b6' }}>
                      {fmtEur(totalAchats)}
                    </div>
                  </div>

                  {/* Marge */}
                  <div style={{ background: margeBg, borderRadius: '12px', padding: '16px', border: `1px solid ${margeBorder}` }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: margeColor, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                      Marge Brute
                    </div>
                    <div style={{ fontSize: '1.4rem', fontWeight: '800', color: margeColor }}>
                      {marge != null ? fmtEur(marge) : '—'}
                    </div>
                    {margePercent != null && (
                      <div style={{ fontSize: '1rem', fontWeight: '700', color: margeColor, marginTop: '4px' }}>
                        {margePercent.toFixed(1)}%
                      </div>
                    )}
                    {devisAmount == null && (
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '4px', fontStyle: 'italic' }}>
                        Saisir le devis pour calculer
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Cartes statistiques */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            {[
              { label: 'Bureau d\'Étude', value: projectHistory.totalHours.etude, color: '#3b82f6', bg: '#eff6ff' },
              { label: 'Suivi Chantier', value: projectHistory.totalHours.suivi, color: '#f59e0b', bg: '#fffbeb' },
              { label: 'Mise en Service', value: projectHistory.totalHours.mise_en_service, color: '#8b5cf6', bg: '#f5f3ff' },
              { label: 'Production Chantier', value: projectHistory.totalHours.chantier, color: '#10b981', bg: '#ecfdf5' }
            ].map(item => (
              <div key={item.label} style={{
                background: 'white',
                borderRadius: '16px',
                padding: '20px',
                border: `1px solid ${item.color}30`,
                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
              }}>
                <div style={{
                  width: '10px', height: '10px', borderRadius: '3px',
                  background: item.color, marginBottom: '10px'
                }} />
                <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', marginBottom: '6px' }}>
                  {item.label}
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: '800', color: item.color }}>
                  {item.value.toFixed(2)}
                  <span style={{ fontSize: '0.9rem', fontWeight: '500', marginLeft: '4px', color: '#94a3b8' }}>h</span>
                </div>
              </div>
            ))}
          </div>

          {/* Jauges de progression */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
            {/* Jauge Chantier */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '20px',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <HardHat size={20} color="#10b981" />
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>Budget Chantier</span>
                </div>
                <span style={{
                  fontWeight: '700',
                  color: projectHistory.progressChantier > 100 ? '#ef4444' : projectHistory.progressChantier > 80 ? '#f59e0b' : '#10b981'
                }}>
                  {projectHistory.totalHours.chantier.toFixed(2)} / {projectHistory.budgetChantier} h
                </span>
              </div>
              <div style={{ height: '12px', background: '#f1f5f9', borderRadius: '6px', overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.min(projectHistory.progressChantier, 100)}%`,
                  height: '100%',
                  background: projectHistory.progressChantier > 100 ? '#ef4444' : projectHistory.progressChantier > 80 ? '#f59e0b' : '#10b981',
                  borderRadius: '6px',
                  transition: 'width 0.5s ease'
                }} />
              </div>
              <div style={{ textAlign: 'right', fontSize: '0.85rem', color: '#64748b', marginTop: '6px' }}>
                {projectHistory.budgetChantier > 0 ? `${projectHistory.progressChantier.toFixed(1)}%` : 'Pas de budget défini'}
              </div>
            </div>

            {/* Jauge BE */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '20px',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Briefcase size={20} color="#3b82f6" />
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>Budget Bureau d'Étude</span>
                </div>
                <span style={{
                  fontWeight: '700',
                  color: projectHistory.progressBE > 100 ? '#ef4444' : projectHistory.progressBE > 80 ? '#f59e0b' : '#3b82f6'
                }}>
                  {(projectHistory.totalHours.etude + projectHistory.totalHours.suivi).toFixed(2)} / {projectHistory.budgetBE} h
                </span>
              </div>
              <div style={{ height: '12px', background: '#f1f5f9', borderRadius: '6px', overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.min(projectHistory.progressBE, 100)}%`,
                  height: '100%',
                  background: projectHistory.progressBE > 100 ? '#ef4444' : projectHistory.progressBE > 80 ? '#f59e0b' : '#3b82f6',
                  borderRadius: '6px',
                  transition: 'width 0.5s ease'
                }} />
              </div>
              <div style={{ textAlign: 'right', fontSize: '0.85rem', color: '#64748b', marginTop: '6px' }}>
                {projectHistory.budgetBE > 0 ? `${projectHistory.progressBE.toFixed(1)}%` : 'Pas de budget défini'}
              </div>
            </div>
          </div>

          {/* Graphique timeline */}
          {historyChartData && projectHistory.monthlyData.length > 0 && (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              border: '1px solid #e2e8f0'
            }}>
              <h3 style={{ margin: '0 0 20px', fontSize: '1.1rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <TrendingUp size={20} color="#059669" />
                {showCumulative ? 'Évolution cumulative des heures' : 'Répartition des heures par période'}
              </h3>
              <div style={{ height: '350px' }}>
                <Bar
                  data={historyChartData}
                  options={{
                    maintainAspectRatio: false,
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'bottom' as const,
                        labels: {
                          usePointStyle: true,
                          padding: 20
                        }
                      }
                    },
                    scales: {
                      x: {
                        stacked: !showCumulative,
                        grid: { display: false }
                      },
                      y: {
                        stacked: !showCumulative,
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Heures'
                        },
                        grid: { color: '#f1f5f9' }
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* Tableau détaillé mensuel */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
          }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>
                Détail {historyViewMode === 'monthly' ? 'mensuel' : 'annuel'}
              </h3>
            </div>
            {projectHistory.monthlyData.length === 0 ? (
              <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
                <Calendar size={40} style={{ marginBottom: '12px', opacity: 0.3 }} />
                <p style={{ margin: 0 }}>Aucune entrée de temps validée pour ce projet</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={{ padding: '14px 20px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '0.8rem' }}>
                        {historyViewMode === 'monthly' ? 'Mois' : 'Année'}
                      </th>
                      <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#3b82f6', fontSize: '0.8rem' }}>Étude</th>
                      <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#f59e0b', fontSize: '0.8rem' }}>Suivi</th>
                      <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#8b5cf6', fontSize: '0.8rem' }}>MES</th>
                      <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#10b981', fontSize: '0.8rem' }}>Chantier</th>
                      <th style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '700', color: '#1e293b', fontSize: '0.8rem' }}>Total</th>
                      <th style={{ padding: '14px 20px', textAlign: 'right', fontWeight: '700', color: '#059669', fontSize: '0.8rem', background: '#ecfdf5' }}>Cumul</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(historyViewMode === 'yearly' ? aggregateByYear(projectHistory.monthlyData) : projectHistory.monthlyData).map((row, idx) => (
                      <tr key={row.month} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                        <td style={{ padding: '14px 20px', fontWeight: '600', color: '#334155' }}>{row.monthLabel}</td>
                        <td style={{ padding: '14px 16px', textAlign: 'center', color: row.etude > 0 ? '#3b82f6' : '#cbd5e1' }}>
                          {row.etude > 0 ? row.etude.toFixed(2) : '-'}
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'center', color: row.suivi > 0 ? '#f59e0b' : '#cbd5e1' }}>
                          {row.suivi > 0 ? row.suivi.toFixed(2) : '-'}
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'center', color: row.mise_en_service > 0 ? '#8b5cf6' : '#cbd5e1' }}>
                          {row.mise_en_service > 0 ? row.mise_en_service.toFixed(2) : '-'}
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'center', color: row.chantier > 0 ? '#10b981' : '#cbd5e1' }}>
                          {row.chantier > 0 ? row.chantier.toFixed(2) : '-'}
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '700', color: '#1e293b' }}>
                          {row.total.toFixed(2)} h
                        </td>
                        <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: '700', color: '#059669', background: '#ecfdf5' }}>
                          {row.cumulative.toFixed(2)} h
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot style={{ background: '#f1f5f9' }}>
                    <tr>
                      <td style={{ padding: '14px 20px', fontWeight: '800', color: '#1e293b' }}>TOTAL</td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '700', color: '#3b82f6' }}>
                        {projectHistory.totalHours.etude.toFixed(2)}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '700', color: '#f59e0b' }}>
                        {projectHistory.totalHours.suivi.toFixed(2)}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '700', color: '#8b5cf6' }}>
                        {projectHistory.totalHours.mise_en_service.toFixed(2)}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '700', color: '#10b981' }}>
                        {projectHistory.totalHours.chantier.toFixed(2)}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '800', color: '#1e293b', fontSize: '1rem' }}>
                        {projectHistory.totalHours.total.toFixed(2)} h
                      </td>
                      <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: '800', color: '#059669', background: '#d1fae5', fontSize: '1rem' }}>
                        {projectHistory.totalHours.total.toFixed(2)} h
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
